import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { VERTICALS, getVertical } from '../common/verticals';

const { FiPlus, FiEye, FiEyeOff, FiRefreshCw, FiChevronDown, FiCheckCircle, FiSave, FiLogIn, FiLogOut, FiAlertCircle, FiMapPin, FiX, FiUpload, FiTrash2, FiLink, FiImage } = FiIcons;

const TABLE_CITIES = 'cities_1715634000000';
const TABLE_SLOTS  = 'slots_1715634000000';
const AD_BUCKET    = 'slot-ads';

// ── Auth Gate ────────────────────────────────────────────────────────────────
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      setError(err.message);
    } else {
      onLogin();
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
      <form
        onSubmit={handleLogin}
        className="bg-white border-4 border-brand-brown rounded-[2.5rem] p-10 shadow-[16px_16px_0px_#4A3320] w-full max-w-sm"
      >
        <h1 className="text-3xl font-black text-brand-brown uppercase italic tracking-tighter mb-2">Admin Login</h1>
        <p className="text-brand-brown/50 text-xs font-bold uppercase tracking-widest mb-8">Mailbox Digital — Admin</p>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border-2 border-red-300 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm font-bold">
            <SafeIcon icon={FiAlertCircle} />
            {error}
          </div>
        )}

        <div className="space-y-4 mb-6">
          <input
            type="email"
            placeholder="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-brand-cream/60 border-2 border-brand-brown/10 rounded-xl px-4 py-3 font-bold outline-none focus:border-brand-blue transition-colors"
          />
          <input
            type="password"
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-brand-cream/60 border-2 border-brand-brown/10 rounded-xl px-4 py-3 font-bold outline-none focus:border-brand-blue transition-colors"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-brand-brown text-white py-3 rounded-xl font-black uppercase tracking-tight hover:bg-brand-dark transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiLogIn} />
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ── Slot Card ────────────────────────────────────────────────────────────────
function SlotCard({ slot, onUpdate }) {
  const [category,    setCategory]    = useState(slot.category    ?? '');
  const [description, setDescription] = useState(slot.description ?? '');
  const [websiteUrl,  setWebsiteUrl]  = useState(slot.website_url ?? '');
  const [adImageUrl,  setAdImageUrl]  = useState(slot.ad_image_url ?? '');
  const [saving,      setSaving]      = useState(false);
  const [saveStatus,  setSaveStatus]  = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [expanded,    setExpanded]    = useState(false);
  const imgRef = React.useRef(null);

  useEffect(() => {
    setCategory(slot.category    ?? '');
    setDescription(slot.description ?? '');
    setWebsiteUrl(slot.website_url  ?? '');
    setAdImageUrl(slot.ad_image_url ?? '');
  }, [slot]);

  const saveFields = async () => {
    setSaving(true);
    setSaveStatus(null);
    const ok = await onUpdate(slot.id, {
      category,
      description,
      website_url: websiteUrl,
    });
    setSaveStatus(ok ? 'ok' : 'err');
    setSaving(false);
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const toggleReserved = () => onUpdate(slot.id, { is_reserved: !slot.is_reserved });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext  = file.name.split('.').pop();
    const path = `${slot.id}/ad.${ext}`;
    const { error: upErr } = await supabase.storage
      .from(AD_BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) { setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from(AD_BUCKET).getPublicUrl(path);
    const url = `${publicUrl}?t=${Date.now()}`;
    await onUpdate(slot.id, { ad_image_url: url });
    setAdImageUrl(url);
    setUploading(false);
    if (imgRef.current) imgRef.current.value = '';
  };

  const removeImage = async () => {
    await onUpdate(slot.id, { ad_image_url: '' });
    setAdImageUrl('');
  };

  return (
    <div className={`bg-white border-4 rounded-[2rem] transition-all group ${slot.is_reserved ? 'border-brand-blue shadow-[8px_8px_0px_#009FE3]' : 'border-brand-brown/20'}`}>
      {/* Header row */}
      <div className="flex justify-between items-center p-5 pb-4">
        <span className="bg-brand-brown/5 text-brand-brown/40 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">
          #{slot.position}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(x => !x)}
            className="text-[10px] font-black uppercase tracking-widest text-brand-brown/30 hover:text-brand-blue transition-colors px-2"
          >
            {expanded ? 'Less' : 'Details'}
          </button>
          <button
            onClick={toggleReserved}
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${slot.is_reserved ? 'bg-brand-blue text-white' : 'bg-brand-cream text-brand-brown/20 hover:text-brand-brown'}`}
          >
            <SafeIcon icon={FiCheckCircle} className="text-lg" />
          </button>
        </div>
      </div>

      <div className="px-5 pb-5 space-y-3">
        {/* Category */}
        <div>
          <label className="text-[10px] font-black uppercase text-brand-brown/40 block mb-1">Category</label>
          <div className="relative">
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onBlur={saveFields}
              onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
              className="w-full bg-brand-cream/50 border-2 border-brand-brown/5 rounded-xl px-4 py-2 font-black text-brand-brown uppercase outline-none focus:border-brand-blue transition-colors pr-10 text-sm"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {saving  && <SafeIcon icon={FiRefreshCw} className="animate-spin text-brand-brown/30 text-sm" />}
              {!saving && saveStatus === 'ok'  && <SafeIcon icon={FiCheckCircle} className="text-green-500 text-sm" />}
              {!saving && saveStatus === 'err' && <SafeIcon icon={FiAlertCircle} className="text-red-500 text-sm" />}
              {!saving && !saveStatus           && <SafeIcon icon={FiSave} className="text-brand-brown/20 group-hover:text-brand-blue transition-colors text-sm" />}
            </span>
          </div>
        </div>

        {/* Expanded: description + website + ad image */}
        {expanded && (
          <>
            {/* Description */}
            <div>
              <label className="text-[10px] font-black uppercase text-brand-brown/40 block mb-1">Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={saveFields}
                placeholder="Short description shown in the digital directory…"
                className="w-full bg-brand-cream/50 border-2 border-brand-brown/5 rounded-xl px-4 py-2 font-bold text-brand-brown outline-none focus:border-brand-blue transition-colors text-sm resize-none"
              />
            </div>

            {/* Website URL */}
            <div>
              <label className="text-[10px] font-black uppercase text-brand-brown/40 block mb-1">Website URL</label>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiLink} className="text-brand-brown/30 shrink-0" />
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  onBlur={saveFields}
                  placeholder="https://theirbusiness.com"
                  className="w-full bg-brand-cream/50 border-2 border-brand-brown/5 rounded-xl px-3 py-2 font-bold text-brand-brown outline-none focus:border-brand-blue transition-colors text-sm"
                />
              </div>
            </div>

            {/* Ad Image */}
            <div>
              <label className="text-[10px] font-black uppercase text-brand-brown/40 block mb-2">
                <SafeIcon icon={FiImage} className="inline mr-1" /> Ad Image (digital directory)
              </label>
              {adImageUrl ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-brand-brown/10 mb-2">
                  <img src={adImageUrl} alt="Ad" className="w-full h-32 object-cover" />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-600 transition-all"
                  >
                    <SafeIcon icon={FiTrash2} className="text-xs" />
                  </button>
                </div>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-brand-brown/20 bg-brand-cream/40 p-4 text-center text-brand-brown/30 font-bold text-xs mb-2">
                  No ad image yet
                </div>
              )}
              <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <button
                onClick={() => imgRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 bg-brand-brown/10 text-brand-brown py-2 rounded-xl font-black uppercase text-xs hover:bg-brand-blue hover:text-white transition-all disabled:opacity-50"
              >
                <SafeIcon icon={uploading ? FiRefreshCw : FiUpload} className={uploading ? 'animate-spin' : ''} />
                {uploading ? 'Uploading…' : adImageUrl ? 'Replace Image' : 'Upload Ad Image'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Zip Map Uploader ──────────────────────────────────────────────────────────
const STORAGE_BUCKET = 'zip-maps';

function ZipMapUploader({ city, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [status, setStatus]       = useState(null); // 'ok' | 'err'
  const [preview, setPreview]     = useState(city.zip_map_url ?? '');
  const inputRef                  = React.useRef(null);

  useEffect(() => {
    setPreview(city.zip_map_url ?? '');
  }, [city.zip_map_url]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setStatus(null);

    const ext      = file.name.split('.').pop();
    const path     = `${city.id}/zip-map.${ext}`;

    // Upload (upsert so re-uploading replaces the old file)
    const { error: uploadErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadErr) {
      console.error(uploadErr);
      setStatus('err');
      setUploading(false);
      return;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(path);

    // Cache-bust so the browser picks up the new image
    const urlWithBust = `${publicUrl}?t=${Date.now()}`;

    // Save to DB
    const { error: dbErr } = await supabase
      .from(TABLE_CITIES)
      .update({ zip_map_url: urlWithBust })
      .eq('id', city.id);

    if (dbErr) {
      setStatus('err');
    } else {
      setPreview(urlWithBust);
      setStatus('ok');
      onSaved();
    }

    setUploading(false);
    setTimeout(() => setStatus(null), 3000);
    // Reset file input so same file can be re-uploaded
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = async () => {
    const { error } = await supabase
      .from(TABLE_CITIES)
      .update({ zip_map_url: '' })
      .eq('id', city.id);
    if (!error) { setPreview(''); onSaved(); }
  };

  return (
    <div className="bg-white border-4 border-brand-brown/10 rounded-[2rem] p-6">
      <div className="flex items-center gap-3 mb-5">
        <SafeIcon icon={FiMapPin} className="text-brand-blue text-xl" />
        <h3 className="font-black text-brand-brown uppercase tracking-tight text-lg">Coverage Area Image</h3>
        <span className="text-[10px] font-black uppercase text-brand-brown/30 tracking-widest">for {city.name}</span>
        <span className="ml-auto flex items-center gap-2">
          {uploading && <><SafeIcon icon={FiRefreshCw} className="animate-spin text-brand-brown/40" /><span className="text-xs font-bold text-brand-brown/40">Uploading…</span></>}
          {!uploading && status === 'ok'  && <><SafeIcon icon={FiCheckCircle} className="text-green-500" /><span className="text-xs font-bold text-green-600">Saved</span></>}
          {!uploading && status === 'err' && <><SafeIcon icon={FiAlertCircle} className="text-red-500" /><span className="text-xs font-bold text-red-600">Upload failed</span></>}
        </span>
      </div>

      {/* Preview */}
      {preview ? (
        <div className="relative mb-5 rounded-2xl overflow-hidden border-2 border-brand-brown/10 bg-brand-cream/40">
          <img src={preview} alt="Zip coverage map" className="w-full max-h-64 object-contain" />
          <button
            onClick={handleRemove}
            className="absolute top-3 right-3 bg-red-500 text-white w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-600 transition-all shadow"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
          </button>
        </div>
      ) : (
        <div className="mb-5 rounded-2xl border-2 border-dashed border-brand-brown/20 bg-brand-cream/40 p-10 text-center text-brand-brown/30 font-bold text-sm">
          No coverage image uploaded yet
        </div>
      )}

      {/* Upload button */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-3 bg-brand-blue text-white py-3 rounded-xl font-black uppercase text-sm hover:bg-brand-dark transition-all disabled:opacity-50"
      >
        <SafeIcon icon={FiUpload} />
        {preview ? 'Replace Image' : 'Upload Coverage Image'}
      </button>
      <p className="text-[10px] text-brand-brown/30 font-bold text-center mt-2 uppercase tracking-widest">
        PNG, JPG, or WebP · Recommended 800×600px or wider
      </p>
    </div>
  );
}

// ── Main Admin ───────────────────────────────────────────────────────────────
export default function Admin() {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [frontSlots, setFrontSlots] = useState([]);
  const [backSlots,  setBackSlots]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [newCityState, setNewCityState] = useState('AZ');
  const [newCityVertical, setNewCityVertical] = useState('event-planning');
  const [globalError, setGlobalError] = useState('');

  // Auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) fetchCities();
  }, [session]);

  useEffect(() => {
    if (selectedCity) fetchSlots(selectedCity.id);
  }, [selectedCity]);

  const fetchCities = async () => {
    setLoading(true);
    const { data } = await supabase.from(TABLE_CITIES).select('*').order('name');
    if (data) {
      setCities(data);
      setSelectedCity(prev => {
        if (!prev && data.length > 0) return data[0];
        if (prev) return data.find(c => c.id === prev.id) ?? prev;
        return prev;
      });
    }
    setLoading(false);
  };

  const fetchSlots = async (cityId) => {
    const { data } = await supabase
      .from(TABLE_SLOTS)
      .select('*')
      .eq('city_id', cityId)
      .order('position');
    if (data) {
      setFrontSlots(data.filter(s => s.side !== 'back'));
      setBackSlots(data.filter(s => s.side === 'back'));
    }
  };

  const createCity = async () => {
    if (!newCityName.trim() || submitting) return;
    setSubmitting(true);
    setGlobalError('');
    const base = newCityName.toLowerCase().trim().replace(/\s+/g, '-');
    const slug = `${base}-${newCityVertical}`;
    try {
      const { data: city, error: cityError } = await supabase
        .from(TABLE_CITIES)
        .insert([{
          name: newCityName.trim(),
          slug,
          is_active: true,
          vertical: newCityVertical,
          state: newCityState.trim().toUpperCase() || 'AZ',
        }])
        .select()
        .single();
      if (cityError) throw cityError;

      const frontSlots = Array.from({ length: 8 }, (_, i) => ({
        city_id: city.id, category: 'Open Slot', position: i + 1,
        is_reserved: false, size: '3" x 4"', side: 'front'
      }));
      const backSlots = Array.from({ length: 10 }, (_, i) => ({
        city_id: city.id, category: 'Open Slot', position: i + 1,
        is_reserved: false, size: '2" x 3"', side: 'back'
      }));
      const { error: slotsError } = await supabase.from(TABLE_SLOTS).insert([...frontSlots, ...backSlots]);
      if (slotsError) throw slotsError;

      setNewCityName('');
      await fetchCities();
      setSelectedCity(city);
    } catch (err) {
      setGlobalError(err.message || 'City name must be unique.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCityActive = async () => {
    if (!selectedCity) return;
    const { error } = await supabase
      .from(TABLE_CITIES)
      .update({ is_active: !selectedCity.is_active })
      .eq('id', selectedCity.id);
    if (error) {
      setGlobalError(error.message);
    } else {
      await fetchCities();
    }
  };

  const generateBackSlots = async () => {
    if (!selectedCity || backSlots.length > 0) return;
    setSubmitting(true);
    const newBack = Array.from({ length: 10 }, (_, i) => ({
      city_id: selectedCity.id, category: 'Open Slot', position: i + 1,
      is_reserved: false, size: '2" x 3"', side: 'back'
    }));
    const { error } = await supabase.from(TABLE_SLOTS).insert(newBack);
    if (error) setGlobalError(error.message);
    else await fetchSlots(selectedCity.id);
    setSubmitting(false);
  };

  // Returns true on success, false on failure
  const updateSlot = async (slotId, updates) => {
    const { error } = await supabase
      .from(TABLE_SLOTS)
      .update(updates)
      .eq('id', slotId);
    if (error) {
      console.error('Slot update error:', error);
      return false;
    }
    await fetchSlots(selectedCity.id);
    return true;
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // Auth loading
  if (session === undefined) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center text-white">
        <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl" />
      </div>
    );
  }

  if (!session) return <LoginForm onLogin={fetchCities} />;

  if (loading) return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center text-white">
      <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl" />
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-cream pt-32 pb-20 px-4">
      <div className="max-w-6xl mx-auto">

        {globalError && (
          <div className="flex items-center gap-2 bg-red-50 border-2 border-red-300 text-red-700 rounded-2xl px-5 py-4 mb-6 font-bold text-sm">
            <SafeIcon icon={FiAlertCircle} className="shrink-0" />
            {globalError}
            <button onClick={() => setGlobalError('')} className="ml-auto text-red-400 hover:text-red-700">✕</button>
          </div>
        )}

        <div className="bg-white border-4 border-brand-brown rounded-[2.5rem] p-8 md:p-12 shadow-[16px_16px_0px_#4A3320] mb-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
            <div>
              <h1 className="text-4xl font-black text-brand-brown uppercase italic tracking-tighter mb-2">Admin Dashboard</h1>
              <p className="text-brand-brown/60 font-bold uppercase tracking-widest text-xs">
                Signed in as {session.user.email}
              </p>
            </div>

            <div className="flex flex-wrap gap-4 w-full lg:w-auto items-center">
              {/* New Edition Form */}
              <div className="bg-brand-cream rounded-2xl border-2 border-brand-brown/10 p-3 flex flex-col gap-2 flex-grow lg:flex-grow-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="City Name"
                    className="bg-white/70 px-3 py-2 rounded-xl font-bold outline-none w-full lg:w-36 text-sm border border-brand-brown/10 focus:border-brand-blue transition-colors"
                    value={newCityName}
                    onChange={(e) => setNewCityName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createCity()}
                  />
                  <input
                    type="text"
                    placeholder="ST"
                    maxLength={2}
                    className="bg-white/70 px-3 py-2 rounded-xl font-bold outline-none w-16 text-sm border border-brand-brown/10 focus:border-brand-blue transition-colors uppercase"
                    value={newCityState}
                    onChange={(e) => setNewCityState(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    className="flex-1 bg-white/70 px-3 py-2 rounded-xl font-bold outline-none text-sm border border-brand-brown/10 focus:border-brand-blue transition-colors"
                    value={newCityVertical}
                    onChange={(e) => setNewCityVertical(e.target.value)}
                  >
                    {VERTICALS.map(v => (
                      <option key={v.id} value={v.id}>{v.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={createCity}
                    disabled={submitting}
                    className="bg-brand-brown text-white px-5 py-2 rounded-xl font-black uppercase text-sm hover:bg-brand-dark transition-all disabled:opacity-50 flex items-center gap-2 shrink-0"
                  >
                    <SafeIcon icon={FiPlus} />
                    {submitting ? '…' : 'Add'}
                  </button>
                </div>
              </div>

              {/* City Selector */}
              <div className="relative flex-grow lg:flex-grow-0">
                <select
                  className="appearance-none bg-brand-blue text-white px-8 py-3 rounded-2xl font-black uppercase tracking-tight outline-none cursor-pointer w-full pr-12"
                  value={selectedCity?.id || ''}
                  onChange={(e) => setSelectedCity(cities.find(c => c.id === e.target.value))}
                >
                  {VERTICALS.map(v => {
                    const group = cities.filter(c => (c.vertical ?? 'event-planning') === v.id);
                    if (group.length === 0) return null;
                    return (
                      <optgroup key={v.id} label={`── ${v.label} ──`}>
                        {group.map(c => (
                          <option key={c.id} value={c.id}>{c.name}, {c.state ?? 'AZ'}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-5 py-3 rounded-2xl border-2 border-brand-brown/20 font-black uppercase text-xs text-brand-brown/60 hover:border-brand-brown hover:text-brand-brown transition-all"
              >
                <SafeIcon icon={FiLogOut} />
                Logout
              </button>
            </div>
          </div>
        </div>

        {selectedCity && (() => {
          const v = getVertical(selectedCity.vertical ?? 'event-planning');
          return (
          <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex items-center justify-between bg-brand-brown text-white p-6 rounded-3xl flex-wrap gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <h2 className="text-2xl font-black uppercase italic">
                  {selectedCity.name}, {selectedCity.state ?? 'AZ'}
                  <span className="text-brand-yellow/50 ml-2 text-base">/{selectedCity.slug}</span>
                </h2>
                <span className={`${v.bg} ${v.text} px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest`}>
                  {v.label}
                </span>
              </div>
              <button
                onClick={toggleCityActive}
                className={`flex items-center gap-2 px-6 py-2 rounded-xl font-black uppercase text-xs transition-all ${selectedCity.is_active ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
              >
                <SafeIcon icon={selectedCity.is_active ? FiEye : FiEyeOff} />
                {selectedCity.is_active ? 'Active' : 'Hidden'}
              </button>
            </div>

            <ZipMapUploader city={selectedCity} onSaved={fetchCities} />

            {/* Front Side */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-brown/40">Front Side</span>
                <div className="flex-1 h-px bg-brand-brown/10" />
                <span className="text-[10px] font-black uppercase tracking-widest text-brand-brown/30">8 Slots · 3″ × 4″</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {frontSlots.map(slot => (
                  <SlotCard key={slot.id} slot={slot} onUpdate={updateSlot} />
                ))}
              </div>
            </div>

            {/* Back Side */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-brown/40">Back Side</span>
                <div className="flex-1 h-px bg-brand-brown/10" />
                <span className="text-[10px] font-black uppercase tracking-widest text-brand-brown/30">10 Slots · 2″ × 3″</span>
              </div>
              {backSlots.length === 0 ? (
                <div className="flex items-center gap-4 bg-white border-4 border-dashed border-brand-brown/10 rounded-[2rem] p-8">
                  <p className="text-brand-brown/40 font-bold flex-1">No back side slots yet for this city.</p>
                  <button
                    onClick={generateBackSlots}
                    disabled={submitting}
                    className="bg-brand-blue text-white px-6 py-3 rounded-xl font-black uppercase text-xs hover:bg-brand-dark transition-all disabled:opacity-50 flex items-center gap-2 shrink-0"
                  >
                    <SafeIcon icon={FiPlus} /> Generate Back Slots
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {backSlots.map(slot => (
                    <SlotCard key={slot.id} slot={slot} onUpdate={updateSlot} />
                  ))}
                </div>
              )}
            </div>
          </div>
        );})()}
      </div>
    </div>
  );
}
